import {
  type Address,
  type CapacityLimit,
  CapacityLimitReason,
  getChainAddresses
} from '@morpho-org/blue-sdk'
import { InterfaceChainUtils, parseApiAsset, useReadOnlyVault, ZERO } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { useTokenBalance } from '@repo/web3'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAddressEqual } from 'viem'
import { useBalance } from 'wagmi'

import { useVaultV2DisabledLabel } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/hooks/useVaultV2DisabledLabel'
import { isTestVault } from '@/constants/testVaultList'
import { useWeb3Context } from '@/contexts/Web3Context'
import { getParsedValue } from '@/helpers/formatters'
import { getDepositDisabledReason } from '@/helpers/warnings'
import useAssetCanTransferOnchain from '@/hooks/operation/useAssetCanTransferOnchain'
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain'
import useVaultV2TooltipMessage from '@/hooks/operation/vault/useVaultV2TooltipMessage'
import { useChangeAfterFetching } from '@/hooks/useChangeAfterFetching'
import { useSpendableBalance } from '@/hooks/useSpendableBalance'
import { useWrappingGasReserve } from '@/hooks/useWrappingGasReserve'
import { computeWrappableNativeBalance } from '@/hooks/wrappingGasReserve.helpers'
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider'

import type { VaultV1ActionPanelData } from '../VaultSimpleActionPanel/vaultV1ActionPanelData'

import {
  computeVaultDepositFunding,
  getVaultDepositBalance,
  isAtUnpricedGasTokenLimit,
  resolveStickyMaxAmount
} from '../../hooks/vaultDeposit.helpers'
import { useVaultV1 } from './useVaultV1'
import { useVaultV1Position } from './useVaultV1Position'

type VaultV1ByAddress = Pick<VaultV1ActionPanelData, 'address' | 'asset' | 'featured' | 'warnings'>

interface UseVaultV1DepositLogicParams {
  vaultData: VaultV1ByAddress | null | undefined
  vaultAddress: Address
}

export function useVaultV1DepositLogic({
  vaultData: vaultDataParam,
  vaultAddress
}: UseVaultV1DepositLogicParams) {
  const { account } = useWeb3Context()
  const { vaultV2WrapDeposit: isWrapDepositEnabled } = useFeatureFlags()

  const vaultAsset = useMemo(
    () => getParsedValue(vaultDataParam?.asset, parseApiAsset),
    [vaultDataParam?.asset]
  )
  const chainId = vaultDataParam?.asset.chain.id
  const isFeaturedVault = vaultDataParam?.featured

  // Product decision: this migrated V1 panel supports depositing the vault asset
  // directly, plus wrapping native -> wNative when the vault asset IS the chain's
  // wNative token. It intentionally does NOT carry over the pre-migration
  // `peripheralTokenOptions` (e.g. staking stETH -> wstETH, or other wrap/stake
  // sources). Only native wrapping is in scope here.
  const isWNativeVault = useMemo(() => {
    if (!isWrapDepositEnabled || !vaultAsset?.address || !chainId) return false
    const { wNative } = getChainAddresses(chainId)
    return !!wNative && isAddressEqual(vaultAsset.address, wNative)
  }, [isWrapDepositEnabled, vaultAsset?.address, chainId])

  const nativeSymbol = useMemo(
    () =>
      chainId === undefined
        ? ''
        : (InterfaceChainUtils.tryGetChain(chainId)?.nativeCurrency.symbol ?? ''),
    [chainId]
  )

  const [rawInputAmount, setInputAmount] = useState(0n)
  const [isMax, setIsMax] = useState(false)
  const [isWrapping, setIsWrapping] = useState(false)

  const { vaultData, isLoading: isVaultLoading, error: vaultDataError } = useVaultV1(vaultAddress)

  const {
    userVaultShares,
    refetch: refetchPosition,
    isLoading: isLoadingPosition,
    error: userVaultPositionError
  } = useVaultV1Position({ vaultAddress, chainId })

  const userVaultAssets = useMemo(() => {
    // Not connected: return 0n so the JS-math final position can still render.
    if (!account.isConnected) return ZERO
    if (!isDefined(userVaultShares)) return undefined
    return vaultData?.toAssets(userVaultShares)
  }, [vaultData, userVaultShares, account.isConnected])

  const {
    data: userBalance,
    isLoading: isLoadingBalance,
    isFetching: isFetchingBalance,
    error: userBalanceError
  } = useTokenBalance({
    chainId,
    userAddress: account.address,
    tokenAddress: vaultAsset?.address,
    refetchInterval: 10_000
  })

  const memoizedUserBalanceError = useChangeAfterFetching(userBalanceError, isFetchingBalance)

  const {
    data: nativeBalanceData,
    isLoading: isLoadingNativeBalance,
    isFetching: isFetchingNativeBalance,
    error: nativeBalanceError
  } = useBalance({
    address: account.address,
    chainId,
    query: {
      enabled: isWNativeVault && account.isConnected,
      refetchInterval: 10_000
    }
  })
  const nativeBalance = nativeBalanceData?.value ?? ZERO
  const memoizedNativeBalanceError = useChangeAfterFetching(
    nativeBalanceError,
    isFetchingNativeBalance
  )

  const {
    reserve: nativeGasReserve,
    isLoading: isLoadingGasReserve,
    isError: isGasReserveError
  } = useWrappingGasReserve({
    chainId,
    enabled: isWNativeVault && account.isConnected
  })
  // A restoring wallet session already reports `isConnected` with its stored address.
  const isNativeBalancePending =
    account.isConnected && (isLoadingNativeBalance || isLoadingGasReserve)

  // Chains paying gas in an ERC-20 that can itself be the vault asset: MAX must hold a gas
  // reserve back, or spending the full balance on the deposit leaves nothing for gas.
  const {
    spendableBalance,
    isGasToken: isGasTokenVault,
    isLoading: isGasTokenReserveLoading,
    isReserveError: isGasTokenReserveError
  } = useSpendableBalance({
    chainId,
    token: vaultAsset?.address,
    balance: userBalance,
    enabled: account.isConnected
  })

  // Permissioned assets (Backed/Coinbase): block deposit when the wallet isn't
  // authorized to transfer the vault asset, mirroring the pre-migration guard.
  const canTransfer = useAssetCanTransferOnchain(account.address, vaultAsset?.address, chainId)

  // Issuer gate on the bundle itself: permissioned vault assets (Backed/Coinbase) must
  // whitelist VaultBundlesV1 — the contract deposits are routed through — or the tx reverts.
  const { canTransfer: bundleCanTransfer } = useBundleCanTransferOnchain(
    'bundles.vaultBundlesV1',
    vaultAsset?.address,
    chainId
  )

  // Portion of native balance safe to wrap without starving the wallet of gas.
  const wrappableNativeBalance = useMemo(
    () => computeWrappableNativeBalance({ nativeBalance, nativeGasReserve }),
    [nativeBalance, nativeGasReserve]
  )

  const maxDeposit = useMemo<CapacityLimit | undefined>(() => {
    const balance = getVaultDepositBalance({
      isWrapping,
      userBalance: spendableBalance,
      wrappableNativeBalance
    })
    if (!isDefined(balance)) return undefined
    // Until the on-chain vault data resolves, fall back to the wallet balance so the max
    // surfaces as soon as the balance does. Once vaultData is available it refines this to the
    // cap-aware max, and the derived input follows while MAX is active.
    if (!isDefined(vaultData)) return { value: balance, limiter: CapacityLimitReason.balance }
    return vaultData.maxDeposit(balance)
  }, [vaultData, spendableBalance, isWrapping, wrappableNativeBalance])

  const inputAmount = resolveStickyMaxAmount(isMax, maxDeposit, rawInputAmount)

  const { erc20Amount, nativeAmount } = useMemo(
    () => computeVaultDepositFunding({ inputAmount, isWrapping }),
    [inputAmount, isWrapping]
  )

  const onMaxClick = useCallback((value: boolean) => {
    setIsMax(value)
  }, [])

  const onWrappingChange = useCallback((value: boolean) => {
    setIsWrapping(value)
  }, [])

  // inputAmount is derived above while MAX is active; this effect only persists the live max
  // into raw state so the amount survives untoggling MAX (typing untoggles isMax, so it never
  // fights user input).
  useEffect(() => {
    if (isMax && isDefined(maxDeposit)) {
      setInputAmount(maxDeposit.value)
    }
  }, [isMax, maxDeposit])

  const finalPosition = useMemo(() => {
    if (!inputAmount || !isDefined(userVaultAssets)) return undefined
    return userVaultAssets + inputAmount
  }, [userVaultAssets, inputAmount])

  const localErrorMessage = useMemo(() => {
    if (isDefined(vaultDataError)) return 'Unable to get vault data'
    // Without a gas price the reserve is unknown, so wrapping is withheld rather than mis-sized.
    if (isWrapping && isGasReserveError && inputAmount > wrappableNativeBalance) {
      return 'Turn off wrapping to continue'
    }
    // Without a gas price the reserve is unknown, so MAX and the full balance are refused
    // instead of mis-sizing them.
    if (
      isAtUnpricedGasTokenLimit({
        isGasToken: isGasTokenVault,
        isReserveError: isGasTokenReserveError,
        userBalance,
        inputAmount,
        isMax
      })
    ) {
      return `Leave some ${vaultAsset?.symbol} for gas`
    }
    if (!isDefined(maxDeposit) || isDefined(memoizedUserBalanceError)) {
      return 'Unable to get vault deposit amount'
    }
    if (!isDefined(userVaultAssets) || isDefined(userVaultPositionError)) {
      return 'Unable to get user vault assets'
    }
    if (inputAmount <= maxDeposit.value) return
    if (maxDeposit.limiter === CapacityLimitReason.balance) {
      return `Insufficient ${vaultAsset?.symbol} balance`
    }
    if (maxDeposit.limiter === CapacityLimitReason.cap) return 'Vault cap reached'
    return 'Maximum deposit amount exceeded'
  }, [
    inputAmount,
    maxDeposit,
    vaultAsset?.symbol,
    userVaultAssets,
    userVaultPositionError,
    memoizedUserBalanceError,
    vaultDataError,
    isWrapping,
    isGasReserveError,
    wrappableNativeBalance,
    isGasTokenVault,
    isGasTokenReserveError,
    userBalance,
    isMax
  ])

  const depositDisabledReason = useMemo(
    () => getDepositDisabledReason(vaultDataParam?.warnings),
    [vaultDataParam?.warnings]
  )
  const isDepositDisabled = depositDisabledReason !== undefined

  const tooltipMessage = useVaultV2TooltipMessage({
    address: vaultAddress,
    error: localErrorMessage,
    isDepositDisabled,
    depositDisabledReason,
    chainId
  })

  const isLoading =
    account.isConnected &&
    [isLoadingPosition, isLoadingBalance, isVaultLoading, isGasTokenReserveLoading].some(Boolean)

  // The max falls back to the wallet balance until the cap-aware vault data resolves, so it
  // surfaces as soon as the balance does — no 0-flash while vault data is still fetching.
  const isMaxLoading =
    account.isConnected && [isLoadingBalance, isGasTokenReserveLoading].some(Boolean)

  // The wrapping toggle changes maxDeposit via the wrappable native balance, so it must wait
  // for the native balance and gas reserve on top of the rest of the panel loading.
  const isWrappingToggleLoading = isLoading || isLoadingNativeBalance || isLoadingGasReserve

  // Read-only vaults (deprecated/migrated) are display-only — keep them non-interactable
  // as the pre-migration flow did. The tooltip already surfaces the read-only reason.
  const { isReadOnly } = useReadOnlyVault({ chainId, address: vaultAddress })

  const baseDisabledLabel = useVaultV2DisabledLabel({
    localErrorMessage,
    isInputEmpty: inputAmount === ZERO,
    isListedVault: isFeaturedVault,
    isDeposit: true,
    isCorrectVaultV2: true,
    isTestVault: isTestVault(chainId, vaultAddress),
    isDepositDisabled
  })

  const disabledLabel = isReadOnly ? (baseDisabledLabel ?? 'Unavailable') : baseDisabledLabel

  const resetInput = useCallback(() => {
    setInputAmount(ZERO)
    setIsMax(false)
    setIsWrapping(false)
  }, [])

  return {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxDeposit,
    finalPosition,
    tooltipMessage,
    isLoading,
    isMaxLoading,
    isWrappingToggleLoading,
    disabledLabel,
    canTransfer,
    bundleCanTransfer,
    userVaultAssets,
    resetInput,
    refetchPosition,
    isWNativeVault,
    nativeSymbol,
    isWrapping,
    onWrappingChange,
    /** Native balance available to wrap after reserving gas. */
    wrappableNativeBalance,
    isNativeBalancePending,
    nativeAmount,
    erc20Amount,
    error: {
      userBalanceError: memoizedUserBalanceError,
      nativeBalanceError: memoizedNativeBalanceError,
      userVaultPositionError
    }
  }
}
